import React, { FC, useCallback, useEffect, useMemo } from "react";
import { Box, CircularProgress, Container, Grid } from "@mui/material";
import ShopLayout1 from "components/layouts/ShopLayout1";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAppSelector } from "hooks/use-app-selector";
import modules from "modules";
import { useRouter } from "next/router";
import parse from "html-react-parser";
import _ from "lodash";
import { ProductsSkeleton } from "components/skeletons";
import useResolution from "hooks/useResolution";
import PromotionalCard from "components/promotional/PromotionalCard";
import { FlexBox } from "components/flex-box";
import { modifyContentWithRegex } from "common";

const { selectors: promotionalSelectors, actions: promotionalActions } =
  modules.promotional;

const PromotionalDetailsPage: FC = () => {
  const { isMobile, isSmallMobile } = useResolution();
  const { query } = useRouter();
  const dispatch = useAppDispatch();
  const details = useAppSelector(promotionalSelectors.selectDetails);
  const detailsContent = useAppSelector(
    promotionalSelectors.selectDetailsContent
  );
  const isLoading = useAppSelector(promotionalSelectors.selectLoading);

  const items = useMemo(() => {
    const extractedItems = details.products;
    return extractedItems?.[0] ? extractedItems : [];
  }, [details]);

  const getDetails = useCallback(() => {
    const { id } = query;
    dispatch(promotionalActions.getPromotionalDetailsThunk({ id: String(id) }));
  }, [dispatch, query]);

  useEffect(() => {
    query?.id && getDetails(); // call endpoint only if important params id has value
  }, [getDetails, query]);

  return (
    <ShopLayout1 title={details.title || "Loading"}>
      <Container
        sx={{
          mb: isMobile ? "40vh" : "10em",
        }}
      >
        {isLoading ? (
          <>
            <FlexBox
              sx={{
                justifyContent: "center",
                my: "2em",
              }}
            >
              <CircularProgress color="secondary" size="9em" />
            </FlexBox>
            <Grid container spacing={1} mt="1em">
              {_.map([1, 2, 3, 4, 5, 6], (item, ind) => (
                <Grid item md={2} sm={4} xs={6} key={ind}>
                  <ProductsSkeleton />
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}

        {detailsContent && !isLoading ? (
          <Box sx={{ mt: "1em", mb: "2em" }}>
            {parse(modifyContentWithRegex(detailsContent, isSmallMobile))}
          </Box>
        ) : null}

        {items[0] && !isLoading && (
          <Grid container spacing={1} mt="1em">
            {items.map((i) => (
              <Grid item lg={2} md={3} sm={4} xs={6} key={i.product_name}>
                <PromotionalCard data={i} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </ShopLayout1>
  );
};

export default PromotionalDetailsPage;
